import { useCallback, useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useFeld, useStellenDaten, type Feld, type Speicherstelle } from '@/lib/speichern'
import { useZeilenStelle } from '../lib/projekt-einstellungen'
import {
  deleteBomCategory,
  deleteImportMapping,
  fetchBomCategories,
  fetchImportMappings,
  saveBomCategory,
  type BomCategory,
  type ImportMapping,
} from '../api/project-bom.api'

/** `inlineInput` ohne `w-full` — sonst überstimmt es jede gesetzte Breite. */
const schmal = inlineInput.replace('w-full', '')

const MODI = [
  { value: 'NONE', label: 'keine Freigabe nötig' },
  { value: 'ORDER_ONLY', label: 'Bestellfreigabe nötig (Vorgabe)' },
  { value: 'INQUIRY_AND_ORDER', label: 'Anfrage- und Bestellfreigabe nötig' },
]

/**
 * AP-35 Detailblatt 5 2.3, 4.3, 6. — **Reiter „Stückliste & Einkauf"** der
 * Einstellungen: welche Freigabe das Einfügen in einen Beleg verlangt, die
 * Kategorien (Montagezubehör als eigener Block in Mappe und Karte, Gewicht
 * zählt oder nicht) und die gespeicherten Import-Zuordnungen.
 *
 * AP-36 Phase D: Freigaben auf der Stelle `projectSettings` (sofort, Auswahl),
 * jede Kategorie eine eigene Stelle (`bomCategory:<id>`).
 */
export function BomSettingsTab({ darfAendern, stelle }: { darfAendern: boolean; stelle: Speicherstelle<unknown> }) {
  const d = useStellenDaten(stelle)
  const freigabe = useFeld(stelle, 'procurementApproval', { art: 'auswahl', label: 'Freigaben', pflicht: true, gesperrt: !darfAendern })
  const modus = String(freigabe.wert ?? d.procurementApproval ?? 'ORDER_ONLY')
  /** AP-35 Nachtrag 6: Vorbelegung neuer Zeilen. */
  const jaNein = { art: 'auswahl' as const, gesperrt: !darfAendern, lies: (x: string) => x === 'ja', schreib: (w: boolean) => (w ? 'ja' : 'nein') }
  const anfrageAuto = useFeld<boolean>(stelle, 'bomInquiryApprovalAuto', { ...jaNein, label: 'Anfragefreigabe' })
  const bestellAuto = useFeld<boolean>(stelle, 'bomOrderApprovalAuto', { ...jaNein, label: 'Bestellfreigabe' })
  /** Mengenbezug einer neuen Zeile (Auftraggeber 30.9.2026). */
  const bezug = useFeld(stelle, 'bomQuantityBasisDefault', { art: 'auswahl', label: 'Mengenbezug', pflicht: true, gesperrt: !darfAendern })
  const [kategorien, setKategorien] = useState<BomCategory[]>([])
  const [zuordnungen, setZuordnungen] = useState<ImportMapping[]>([])
  const [neu, setNeu] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const [k, z] = await Promise.all([fetchBomCategories(), fetchImportMappings()])
      setKategorien(k)
      setZuordnungen(z)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  async function tu(aktion: () => Promise<unknown>) {
    setFehler(null)
    try {
      await aktion()
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  return (
    <div className="space-y-4">
      <div className={`${formCardSm} space-y-2`}>
        <h3 className={cardTitle}>Freigaben</h3>
        <p className="text-sm text-muted-foreground">
          Ohne die geforderte Freigabe lässt sich eine Stücklistenzeile nicht in eine Preisanfrage oder Bestellung einfügen.
        </p>
        <span className="flex items-center gap-1.5">
          <select className={`${schmal} w-80`} aria-label="Freigaben" {...freigabe.props}>
            {MODI.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <FeldStatusZeichen status={freigabe} />
        </span>
        <FeldStatus status={freigabe} zeichen={false} />
        {modus !== 'NONE' && (
          <div className="space-y-1.5 pt-2">
            <p className="text-sm text-muted-foreground">Vorbelegung neuer Stücklistenzeilen — gilt beim Anlegen, Einfügen, Importieren und Kopieren. Eine automatische Freigabe steht ohne Person im Protokoll.</p>
            {modus === 'INQUIRY_AND_ORDER' && <Vorbelegung titel="Anfragefreigabe" feld={anfrageAuto} />}
            <Vorbelegung titel="Bestellfreigabe" feld={bestellAuto} />
          </div>
        )}
      </div>

      <div className={`${formCardSm} space-y-2`}>
        <h3 className={cardTitle}>Mengenbezug neuer Zeilen</h3>
        <p className="text-sm text-muted-foreground">Womit eine neue Stücklistenzeile beginnt — änderbar an jeder Zeile.</p>
        <span className="flex items-center gap-1.5">
          <select className={`${schmal} w-48`} aria-label="Mengenbezug neuer Zeilen" {...bezug.props}>
            <option value="TOTAL">gesamt</option>
            <option value="PER_UNIT">je Einheit</option>
          </select>
          <FeldStatusZeichen status={bezug} />
        </span>
        <FeldStatus status={bezug} zeichen={false} />
      </div>

      <div className={`${formCardSm} space-y-2`}>
        <h3 className={cardTitle}>Kategorien</h3>
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground">
            <tr>
              <th className="px-1 text-left">Name</th>
              <th className="px-1 text-center">Montagezubehör</th>
              <th className="px-1 text-center">Gewicht zählt</th>
              <th className="px-1 text-center">aktiv</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {kategorien.map((k) => (
              <KategorieZeile key={k.id} kategorie={k} darf={darfAendern} onLoeschen={() => void tu(() => deleteBomCategory(k.id))} />
            ))}
          </tbody>
        </table>
        {darfAendern && (
          <div className="flex gap-2">
            <input className={`${schmal} w-64`} placeholder="neue Kategorie" value={neu} onChange={(e) => setNeu(e.target.value)} />
            <button
              type="button"
              disabled={!neu.trim()}
              className="rounded-md border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
              onClick={() =>
                void tu(async () => {
                  await saveBomCategory({ name: neu.trim(), sortOrder: (kategorien.length + 1) * 10 })
                  setNeu('')
                })
              }
            >
              Anlegen
            </button>
          </div>
        )}
      </div>

      <div className={`${formCardSm} space-y-2`}>
        <h3 className={cardTitle}>Import-Zuordnungen</h3>
        <p className="text-sm text-muted-foreground">Entstehen beim Import mit „Zuordnung speichern als …" und werden beim nächsten Import vorgeschlagen.</p>
        {zuordnungen.length === 0 && <p className="text-sm text-muted-foreground">Noch keine.</p>}
        <ul className="text-sm">
          {zuordnungen.map((z) => (
            <li key={z.id} className="flex items-center justify-between border-t border-border py-1">
              <span>
                {z.name} <span className="text-xs text-muted-foreground">({z.kind === 'CAD' ? 'CAD' : 'Tabelle'}, {Object.keys(z.columns).length} Spalten)</span>
              </span>
              {darfAendern && (
                <button type="button" className="p-1 text-muted-foreground hover:text-destructive" aria-label="löschen" onClick={() => void tu(() => deleteImportMapping(z.id))}>
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
    </div>
  )
}

/** Eine Freigabe-Vorbelegung: „automatisch ja" oder „automatisch nein" — speichert sofort. */
function Vorbelegung({ titel, feld }: { titel: string; feld: Feld<boolean> }) {
  return (
    <label className="flex items-center gap-3 text-sm">
      <span className="w-36">{titel}</span>
      <select className={`${schmal} w-56`} {...feld.props}>
        <option value="nein">automatisch nein</option>
        <option value="ja">automatisch ja</option>
      </select>
      <FeldStatusZeichen status={feld} />
      <FeldStatus status={feld} zeichen={false} />
    </label>
  )
}

/** Eine Kategorie — eine Stelle, Name beim Verlassen, Häkchen sofort. */
function KategorieZeile({ kategorie: k, darf, onLoeschen }: { kategorie: BomCategory; darf: boolean; onLoeschen: () => void }) {
  const stelle = useZeilenStelle('bomCategory', `/projects/bom-categories/${k.id}`, k, k.name)
  const name = useFeld(stelle, 'name', { label: 'Name', pflicht: true, gesperrt: !darf })
  return (
    <tr className="border-t border-border align-top">
      <td className="px-1 py-1">
        <span className="flex items-center gap-1">
          <input className={inlineInput} aria-label="Name" {...name.props} />
          <FeldStatusZeichen status={name} />
        </span>
        <FeldStatus status={name} zeichen={false} />
      </td>
      <KategorieHaken stelle={stelle} feld="isAssemblyAccessory" label="Montagezubehör" darf={darf} />
      <KategorieHaken stelle={stelle} feld="countsWeight" label="Gewicht zählt" darf={darf} />
      <KategorieHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
      <td className="px-1 text-right">
        {darf && (
          <button type="button" className="p-1 text-muted-foreground hover:text-destructive" aria-label="löschen" onClick={onLoeschen}>
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  )
}

function KategorieHaken({ stelle, feld, label, darf }: { stelle: Speicherstelle<unknown>; feld: string; label: string; darf: boolean }) {
  const f = useFeld<boolean>(stelle, feld, { art: 'schalter', label, gesperrt: !darf })
  return (
    <td className="px-1 text-center">
      <span className="inline-flex items-center gap-1">
        <input type="checkbox" aria-label={label} {...f.schalter} />
        <FeldStatusZeichen status={f} />
      </span>
      <FeldStatus status={f} zeichen={false} />
    </td>
  )
}
